/** @format */

import {ArrowOutward} from '@mui/icons-material';

import {Trans, useTranslation} from 'react-i18next';

import {divideBySpaces} from 'helpers/numberHelpers';

import {
  BiggerText,
  ContentBox,
  DetailRow,
  DotsBox,
  LinkButton,
  RegularText,
  Root,
  ScrollBox,
} from './styles';
import {getPeriodDateText} from './helpers/getPeriodDateText';
import {isIncludeYear} from './helpers/isIncludeYear';
import {getDetailedDateText} from './helpers/getDetailedDateText';
import dots from './images/connector.svg';
import {generateLink} from '../helpers/generateLink';
import {GROUP_LABEL_KEY_PREFIX} from '../../../helpers/logs/groupLabels';
import {GroupBreakdown, TooltipScatterData, YandexChangeGroup} from '../../../models/scatters';

interface Props {
  data: TooltipScatterData;
  value: number;
}

type GroupRow = {group: YandexChangeGroup; label: string; count: number};

const buildGroupRows = (byGroup: GroupBreakdown, t: (key: string) => string): GroupRow[] => {
  return (Object.keys(byGroup) as YandexChangeGroup[])
    .map(group => ({
      group,
      label: t(`${GROUP_LABEL_KEY_PREFIX}.${group}`),
      count: byGroup[group] ?? 0,
    }))
    .filter(row => row.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'ru'));
};

interface DetailLineProps {
  label: string;
  value: number;
  testId?: string;
}

const DetailLine = ({label, value, testId}: DetailLineProps) => (
  <DetailRow data-testid={testId}>
    <RegularText>{label}</RegularText>
    <DotsBox>
      <img src={dots} alt='' />
    </DotsBox>
    <RegularText>{divideBySpaces(value)}</RegularText>
  </DetailRow>
);

const LogTooltip = ({data, value}: Props) => {
  const {details, startOn, endOn, byGroup} = data;
  const {t} = useTranslation();

  const groupRows = byGroup ? buildGroupRows(byGroup, t) : [];
  const showGroups = groupRows.length > 0;

  const getEventsText = (): JSX.Element => {
    if (showGroups) {
      return <Trans>Всего изменений</Trans>;
    }

    return endOn ? <Trans>Всего событий</Trans> : <Trans>Событий</Trans>;
  };

  const getLinkButtonText = (): JSX.Element => {
    return endOn ? (
      <Trans>Логи за период</Trans>
    ) : (
      <>
        <Trans>Логи за</Trans>&nbsp;{getPeriodDateText({endOn})}
      </>
    );
  };

  const includeYear = isIncludeYear({startOn, endOn});

  const renderRows = (): JSX.Element | null => {
    if (showGroups) {
      return (
        <ScrollBox elementsCount={groupRows.length}>
          {groupRows.map(row => (
            <DetailLine
              key={row.group}
              label={row.label}
              value={row.count}
              testId='log-group-row'
            />
          ))}
        </ScrollBox>
      );
    }

    if (details?.length) {
      return (
        <ScrollBox elementsCount={details.length}>
          {details.map(detail => (
            <DetailLine
              key={detail.date}
              label={getDetailedDateText(detail.date, includeYear)}
              value={detail.value}
            />
          ))}
        </ScrollBox>
      );
    }

    return null;
  };

  return (
    <Root data-testid='log-tooltip'>
      <ContentBox>
        <BiggerText>{getPeriodDateText({startOn, endOn})}</BiggerText>
        <RegularText>
          {getEventsText()}: {divideBySpaces(value)}
        </RegularText>
      </ContentBox>

      {renderRows()}

      <ContentBox>
        <LinkButton
          href={generateLink({startDate: startOn, endDate: endOn})}
          rel='noopener noreferrer'
          target='_blank'
          data-testid='log-tooltip-link'
        >
          <ArrowOutward fontSize='small' />
          {getLinkButtonText()}
        </LinkButton>
      </ContentBox>
    </Root>
  );
};

export {LogTooltip};
